import { httpsCallable } from 'firebase/functions'
import { doc, updateDoc } from 'firebase/firestore'
import { db, functions } from './firebase'

export interface CreateUserData {
  email: string
  password: string
  firstName: string
  lastName: string
  role: string
  businessId: string
  sendWelcomeEmail?: boolean
}

interface AdminResponse {
  success: boolean
  message?: string
  uid?: string
  /** adminResetPassword only: the generated link, for the admin to pass on. */
  resetLink?: string
}

export const adminCreateUser = httpsCallable<CreateUserData, AdminResponse>(
  functions,
  'adminCreateUser'
)

export const adminResetPassword = httpsCallable<{ uid: string }, AdminResponse>(
  functions,
  'adminResetPassword'
)

export const adminDisableUser = httpsCallable<{ uid: string; disabled: boolean }, AdminResponse>(
  functions,
  'adminDisableUser'
)

export const adminDeleteUser = httpsCallable<{ uid: string }, AdminResponse>(
  functions,
  'adminDeleteUser'
)

/**
 * Create a user through `adminCreateUser`, then make sure the profile carries the
 * `name` field the apps display (iOS/Android map their display name to `name`).
 * Older deployments of the function only write `displayName`; the follow-up write is
 * idempotent once the function writes `name` itself, and best effort — the account
 * already exists if it fails.
 */
export async function createUserWithProfile(data: CreateUserData): Promise<string | undefined> {
  const result = await adminCreateUser(data)
  const uid = result.data?.uid
  if (uid) {
    const fullName = `${data.firstName} ${data.lastName}`.trim()
    try {
      await updateDoc(doc(db, 'users', uid), { name: fullName, displayName: fullName })
    } catch (error) {
      console.warn('Created the user but could not set their display name:', error)
    }
  }
  return uid
}
